import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, apiClient } from '../../utils/api';
import {
    Button, Badge, Card, Modal, Alert, SearchBar, Spinner, Select, Textarea, Input, RichSelect,
} from './shared';

const TIER_BADGES = {
    free: 'default',
    pro: 'info',
    enterprise: 'purple',
};

const fmtDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtCost = (n) => {
    if (n == null || n === 0) return '$0.00';
    return `$${n.toFixed(2)}`;
};

export default function AdminUserTiers() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [distribution, setDistribution] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [tierFilter, setTierFilter] = useState('');
    const [offset, setOffset] = useState(0);
    const [tierOptions, setTierOptions] = useState([]);
    const [editUser, setEditUser] = useState(null);
    const [bulkOpen, setBulkOpen] = useState(false);
    const limit = 50;

    useEffect(() => { loadAll(); }, []);
    useEffect(() => { loadUsers(); }, [tierFilter, offset]);

    const loadAll = async () => {
        try {
            setLoading(true);
            const [usersRes, distRes, tiersRes] = await Promise.all([
                adminApi.listUserTiers({ limit, offset: 0 }),
                adminApi.getTierDistribution(),
                apiClient.listTierWindowLimits(),
            ]);
            setUsers(usersRes.users || []);
            setTotal(usersRes.total || 0);
            setDistribution(distRes.distribution || []);
            const uniqueTiers = [...new Set((tiersRes.items || []).map(t => t.tier_name))];
            setTierOptions(uniqueTiers.map(t => ({ value: t, label: t })));
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleClearAllCache = async () => {
        if (!confirm('Clear tất cả budget cache cho tất cả users? (Tier, usage, adjustments — KHÔNG bao gồm system config)')) return;
        try {
            const res = await apiClient.clearAllBudgetCache();
            setSuccessMessage(res.message || 'Cache cleared');
        } catch (err) {
            setError(err.message || 'Failed to clear cache');
        }
    };

    const [manageTiersUser, setManageTiersUser] = useState(null);
    const [historyUser, setHistoryUser] = useState(null);
    const [historyData, setHistoryData] = useState(null);

    const handleShowHistory = async (userId) => {
        try {
            const res = await apiClient.getUserTierHistory(userId);
            setHistoryData(res);
            setHistoryUser(userId);
        } catch (err) {
            setError(err.message || 'Failed to load history');
        }
    };

    const loadUsers = async () => {
        try {
            const res = await adminApi.listUserTiers({
                tier_name: tierFilter || undefined,
                search: searchQuery || undefined,
                limit, offset,
            });
            setUsers(res.users || []);
            setTotal(res.total || 0);
        } catch {}
    };

    const handleSearch = () => { setOffset(0); loadUsers(); };

    const handleSetTier = async (userId, tierName) => {
        try {
            await adminApi.setUserTier(userId, { tier_name: tierName });
            setSuccessMessage(`User "${userId}" set to tier "${tierName}"`);
            setEditUser(null);
            loadUsers();
            adminApi.getTierDistribution().then(r => setDistribution(r.distribution || [])).catch(() => {});
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleBulkSet = async (userIds, tierName) => {
        try {
            const res = await adminApi.bulkSetTier({ user_ids: userIds, tier_name: tierName });
            setSuccessMessage(`Bulk assign: ${res.created} created, ${res.updated} updated to "${tierName}"`);
            setBulkOpen(false);
            loadUsers();
            adminApi.getTierDistribution().then(r => setDistribution(r.distribution || [])).catch(() => {});
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDelete = async (userId) => {
        if (!confirm(`Remove tier assignment for "${userId}"?`)) return;
        try {
            await adminApi.deleteUserTier(userId);
            setSuccessMessage(`Tier removed for "${userId}"`);
            loadUsers();
            adminApi.getTierDistribution().then(r => setDistribution(r.distribution || [])).catch(() => {});
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const totalPages = Math.ceil(total / limit);
    const page = Math.floor(offset / limit) + 1;

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <h1 className="text-lg font-semibold text-gray-900">Users</h1>
                    <span className="text-xs text-gray-400">{total}</span>
                    {distribution.length > 0 && (
                        <div className="flex items-center gap-1.5">
                            {distribution.map(d => (
                                <Badge key={d.tier_name} variant={TIER_BADGES[d.tier_name] || 'default'}>
                                    {d.tier_name} <span className="font-bold">{d.user_count}</span>
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => setBulkOpen(true)}>Bulk Assign</Button>
                    <Button size="sm" variant="secondary" onClick={handleClearAllCache}>Clear All Cache</Button>
                </div>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">{successMessage}</Alert>}

            <div className="flex flex-wrap items-center gap-2 mb-4">
                <SearchBar
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onSearch={handleSearch}
                    onRefresh={loadAll}
                    placeholder="Search user ID..."
                    className="flex-1"
                />
                <select
                    value={tierFilter}
                    onChange={(e) => { setTierFilter(e.target.value); setOffset(0); }}
                    className="h-8 px-3 text-xs border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-gray-400"
                >
                    <option value="">All Tiers</option>
                    {tierOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
            </div>

            {loading ? (
                <Spinner size="lg" text="Loading user tiers..." center />
            ) : users.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">No users found</div>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">User ID</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Tier</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Sessions</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Total Cost</th>
                                <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Usage</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Last Active</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Created</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {users.map((u) => {
                                const ul = u.usage_limit;
                                const barColor = !ul ? 'bg-gray-200' : !ul.is_within_limit ? 'bg-red-500' : ul.utilization >= 80 ? 'bg-amber-500' : 'bg-green-500';
                                return (
                                    <tr
                                        key={u.user_id}
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={() => navigate(`/admin/users/${u.user_id}`)}
                                    >
                                        <td className="px-4 py-2.5 font-mono text-xs">{u.user_id}</td>
                                        <td className="px-4 py-2.5">
                                            <Badge variant={TIER_BADGES[u.tier_name] || 'default'}>{u.tier_name}</Badge>
                                        </td>
                                        <td className="px-4 py-2.5 text-right text-gray-700">{u.session_count || 0}</td>
                                        <td className="px-4 py-2.5 text-right text-gray-700">{fmtCost(u.total_cost)}</td>
                                        <td className="px-4 py-2.5 w-32">
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                    <div className={`h-full ${barColor} rounded-full`} style={{ width: `${Math.min(ul?.utilization || 0, 100)}%` }} />
                                                </div>
                                                <span className="text-[10px] text-gray-500 w-8 text-right shrink-0">{ul?.utilization || 0}%</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-gray-500">{fmtDate(u.last_active)}</td>
                                        <td className="px-4 py-2.5 text-xs text-gray-500">{fmtDate(u.created_at)}</td>
                                        <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                                            <Button variant="ghost" size="xs" onClick={() => setManageTiersUser(u)}>Manage Tiers</Button>
                                            <Button variant="ghost" size="xs" onClick={() => handleShowHistory(u.user_id)}>History</Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100">
                            <span className="text-xs text-gray-400">{total} results</span>
                            <div className="flex items-center gap-1">
                                <Button variant="ghost" size="xs" disabled={page <= 1} onClick={() => setOffset((page - 2) * limit)}>Prev</Button>
                                <span className="text-xs text-gray-500 px-2">{page} / {totalPages}</span>
                                <Button variant="ghost" size="xs" disabled={page >= totalPages} onClick={() => setOffset(page * limit)}>Next</Button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {editUser && (
                <EditTierModal
                    user={editUser}
                    tierOptions={tierOptions}
                    onSave={(tierName) => handleSetTier(editUser.user_id, tierName)}
                    onClose={() => setEditUser(null)}
                />
            )}

            {bulkOpen && (
                <BulkAssignModal
                    tierOptions={tierOptions}
                    onSave={handleBulkSet}
                    onClose={() => setBulkOpen(false)}
                />
            )}

            {manageTiersUser && (
                <ManageTiersModal
                    user={manageTiersUser}
                    tierOptions={tierOptions}
                    onClose={() => { setManageTiersUser(null); loadAll(); }}
                />
            )}

            {historyUser && historyData && (
                <HistoryModal
                    data={historyData}
                    onClose={() => { setHistoryUser(null); setHistoryData(null); }}
                />
            )}
        </div>
    );
}


function ManageTiersModal({ user, tierOptions, onClose }) {
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [historyData, setHistoryData] = useState(null);

    const loadAssignments = async () => {
        setLoading(true);
        try {
            const res = await apiClient.listTierAssignments(user.user_id);
            setAssignments(res.items || []);
        } catch {}
        setLoading(false);
    };

    const handleShowHistory = async () => {
        try {
            const res = await apiClient.getUserTierHistory(user.user_id);
            setHistoryData(res);
        } catch {}
    };

    useEffect(() => { loadAssignments(); }, []);

    const handleRevoke = async (id) => {
        if (!confirm('Revoke this tier?')) return;
        try {
            await apiClient.deleteTierAssignment(id);
            loadAssignments();
        } catch {}
    };

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('vi-VN') : '—';

    return (
        <>
        <Modal open onClose={onClose} title={`Manage Tiers: ${user.user_id}`} size="lg">
            <div className="p-6 space-y-5">
                <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500">Active tier assignments:</div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleShowHistory}
                            className="p-1.5 text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                            title="View history"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                            </svg>
                        </button>
                        <Button size="sm" onClick={() => setShowAddModal(true)}>+ Add Tier</Button>
                    </div>
                </div>

                {loading ? <div className="text-center text-gray-400 py-4">Loading...</div> : (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 border-b text-left text-gray-500">
                                    <th className="px-3 py-2">Tier</th>
                                    <th className="px-3 py-2">Source</th>
                                    <th className="px-3 py-2">Assigned</th>
                                    <th className="px-3 py-2">Expires</th>
                                    <th className="px-3 py-2">Active</th>
                                    <th className="px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {assignments.map(a => (
                                    <tr key={a.id} className="border-b border-gray-100">
                                        <td className="px-3 py-2 font-medium">{a.tier_name}</td>
                                        <td className="px-3 py-2 text-gray-500 text-xs">{a.source}</td>
                                        <td className="px-3 py-2 text-gray-500 text-xs">{fmtDate(a.assigned_at)}</td>
                                        <td className="px-3 py-2 text-gray-500 text-xs">{fmtDate(a.expires_at)}</td>
                                        <td className="px-3 py-2">
                                            <span className={`w-2 h-2 rounded-full inline-block ${a.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                                        </td>
                                        <td className="px-3 py-2">
                                            <button onClick={() => handleRevoke(a.id)} className="text-red-500 hover:text-red-700 text-xs">Revoke</button>
                                        </td>
                                    </tr>
                                ))}
                                {assignments.length === 0 && (
                                    <tr><td colSpan={6} className="px-3 py-4 text-center text-gray-400">No assignments</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </Modal>

        {showAddModal && (
            <AddTierModal
                userId={user.user_id}
                tierOptions={tierOptions}
                onClose={() => setShowAddModal(false)}
                onAdded={() => { setShowAddModal(false); loadAssignments(); }}
            />
        )}

        {historyData && (
            <HistoryModal
                data={historyData}
                onClose={() => setHistoryData(null)}
            />
        )}
        </>
    );
}

function AddTierModal({ userId, tierOptions, onClose, onAdded }) {
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ tier_name: '', duration_days: '', note: '' });

    const richTierOptions = [{ value: 'free', label: 'free', desc: 'Gói mặc định' }, ...tierOptions.map(t => ({
        value: t.value, label: t.label, desc: `Tier: ${t.value}`,
    }))];

    const handleSave = async () => {
        if (!form.tier_name) return;
        setSaving(true);
        try {
            await apiClient.createTierAssignment({
                user_id: userId,
                tier_name: form.tier_name,
                duration_days: form.duration_days ? parseInt(form.duration_days) : undefined,
                note: form.note || undefined,
            });
            onAdded();
        } catch {}
        setSaving(false);
    };

    return (
        <Modal open onClose={onClose} title={`Add Tier: ${userId}`} size="md">
            <div className="p-6 space-y-4">
                <RichSelect
                    label="Tier"
                    value={form.tier_name}
                    onChange={(v) => setForm({ ...form, tier_name: v })}
                    options={richTierOptions}
                    placeholder="Select tier"
                />
                <Input
                    label="Duration (days)"
                    type="number"
                    value={form.duration_days}
                    onChange={(e) => setForm({ ...form, duration_days: e.target.value })}
                    placeholder="Để trống = không giới hạn thời gian"
                />
                <Textarea
                    label="Note"
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    rows={2}
                    placeholder="Reason"
                />
                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSave} disabled={saving || !form.tier_name}>
                        {saving ? 'Adding...' : 'Add'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

const ACTION_COLORS = {
    assigned: 'text-blue-600',
    upgraded: 'text-green-600',
    downgraded: 'text-orange-600',
    expired: 'text-red-600',
};

function HistoryModal({ data, onClose }) {
    return (
        <Modal open onClose={onClose} title={`Tier History: ${data.user_id}`} size="lg">
            <div className="p-6">
                {data.current && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm space-y-1">
                        <div>Current: <Badge variant="info">{data.current.tier_name}</Badge></div>
                        {data.current.expires_at && (
                            <div className="text-gray-500">Expires: {new Date(data.current.expires_at).toLocaleString('vi-VN')}</div>
                        )}
                        {data.current.fallback_tier && data.current.expires_at && (
                            <div className="text-gray-500">Fallback: {data.current.fallback_tier}</div>
                        )}
                        <div className="text-gray-500">Assigned by: {data.current.assigned_by}</div>
                        {data.had_trial && <div className="text-amber-600 text-xs">User đã dùng trial trước đó</div>}
                    </div>
                )}

                {data.history.length > 0 ? (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 text-left text-gray-500">
                                <th className="px-3 py-2">Time</th>
                                <th className="px-3 py-2">Change</th>
                                <th className="px-3 py-2">Action</th>
                                <th className="px-3 py-2">Source</th>
                                <th className="px-3 py-2">Note</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.history.map(h => (
                                <tr key={h.id} className="border-b border-gray-50">
                                    <td className="px-3 py-2 text-xs text-gray-500">{new Date(h.created_at).toLocaleString('vi-VN')}</td>
                                    <td className="px-3 py-2 text-xs">
                                        <span className="text-gray-400">{h.tier_from || '—'}</span>
                                        <span className="mx-1">→</span>
                                        <span className="font-medium">{h.tier_to}</span>
                                    </td>
                                    <td className={`px-3 py-2 text-xs font-medium ${ACTION_COLORS[h.action] || ''}`}>{h.action}</td>
                                    <td className="px-3 py-2 text-xs text-gray-500">{h.source || '—'}</td>
                                    <td className="px-3 py-2 text-xs text-gray-500 max-w-[200px] truncate">{h.note || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="text-center py-8 text-gray-400 text-sm">No history</div>
                )}
            </div>
        </Modal>
    );
}

function EditTierModal({ user, tierOptions, onSave, onClose }) {
    const [tierName, setTierName] = useState(user.tier_name);
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try { await onSave(tierName); } finally { setSaving(false); }
    };

    return (
        <Modal open onClose={onClose} title={`Change Tier: ${user.user_id}`} size="sm">
            <form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Select
                        label="Tier"
                        options={tierOptions}
                        value={tierName}
                        onChange={(e) => setTierName(e.target.value)}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
                    <Button type="submit" loading={saving}>Save</Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}

function BulkAssignModal({ tierOptions, onSave, onClose }) {
    const [userIdsText, setUserIdsText] = useState('');
    const [tierName, setTierName] = useState(tierOptions[0]?.value || 'free');
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const userIds = userIdsText
            .split(/[\n,]+/)
            .map(id => id.trim())
            .filter(Boolean);
        if (userIds.length === 0) return;
        setSaving(true);
        try { await onSave(userIds, tierName); } finally { setSaving(false); }
    };

    const count = userIdsText.split(/[\n,]+/).map(s => s.trim()).filter(Boolean).length;

    return (
        <Modal open onClose={onClose} title="Bulk Assign Tier">
            <form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Select
                        label="Target Tier"
                        options={tierOptions}
                        value={tierName}
                        onChange={(e) => setTierName(e.target.value)}
                    />
                    <Textarea
                        label={`User IDs (${count} found)`}
                        hint="one per line or comma-separated"
                        rows={8}
                        monospace
                        value={userIdsText}
                        onChange={(e) => setUserIdsText(e.target.value)}
                        placeholder="user_id_1&#10;user_id_2&#10;user_id_3"
                        required
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
                    <Button type="submit" loading={saving} disabled={count === 0}>Assign {count} User{count !== 1 ? 's' : ''}</Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
